import React, { useEffect, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { Spinner } from 'react-bootstrap';

// pdf.js (pdfjs-dist 3.x) draws the SERVER's invoice PDF into canvases, so every browser and every phone shows exactly the file the customer gets.
// The worker is a copy of node_modules/pdfjs-dist/build/pdf.worker.min.js kept in public/ (same version as package.json; a test checks that).
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

const MIN_WIDTH = 320;           // the page always fits the container (a phone shows the whole invoice, pinch-zoom to read details); only below 320px it scrolls sideways
const MAX_PIXEL_RATIO = 3;

// Shows ALL pages of the PDF in `blob`, scaled to the width of the container (re-fitted when the container is resized, e.g. a phone turned
// sideways), one canvas per page. Nothing is fetched here: the blob comes from the authenticated API call of the parent.
export default function InvoicePdfViewer({ blob, label }) {
    const hostRef = useRef(null);
    const docRef = useRef(null);
    const [state, setState] = useState({ status: 'loading', pages: 0 });
    const [width, setWidth] = useState(0);

    // container width (and later changes of it)
    useEffect(() => {
        const host = hostRef.current;
        if (!host) return undefined;
        const measure = () => setWidth(Math.max(MIN_WIDTH, Math.floor(host.clientWidth)));
        measure();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(measure);
        observer.observe(host);
        return () => observer.disconnect();
    }, []);

    // load the document once per blob
    useEffect(() => {
        let cancelled = false;
        setState({ status: 'loading', pages: 0 });
        (async () => {
            try {
                const data = await blob.arrayBuffer();
                const doc = await pdfjsLib.getDocument({ data }).promise;
                if (cancelled) { doc.destroy(); return; }
                docRef.current = doc;
                setState({ status: 'ready', pages: doc.numPages });
            } catch (err) {
                if (!cancelled) setState({ status: 'error', pages: 0 });
            }
        })();
        return () => {
            cancelled = true;
            if (docRef.current) { docRef.current.destroy(); docRef.current = null; }
        };
    }, [blob]);

    // draw every page at the container width
    useEffect(() => {
        const host = hostRef.current;
        const doc = docRef.current;
        if (state.status !== 'ready' || !doc || !host || !width) return undefined;
        let cancelled = false;
        const tasks = [];
        const ratio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
        (async () => {
            for (let n = 1; n <= doc.numPages; n += 1) {
                const page = await doc.getPage(n);
                if (cancelled) return;
                const base = page.getViewport({ scale: 1 });
                const scale = width / base.width;
                const viewport = page.getViewport({ scale: scale * ratio });
                const canvas = host.querySelector(`canvas[data-page="${n}"]`);
                if (!canvas) continue;
                canvas.width = Math.floor(viewport.width);
                canvas.height = Math.floor(viewport.height);
                canvas.style.width = `${width}px`;
                canvas.style.height = `${Math.floor(base.height * scale)}px`;
                canvas.style.maxWidth = 'none';
                const task = page.render({ canvasContext: canvas.getContext('2d'), viewport });
                tasks.push(task);
                try { await task.promise; } catch (err) { /* a cancelled render (resize, unmount) is not an error */ }
            }
        })();
        return () => { cancelled = true; tasks.forEach((t) => { try { t.cancel(); } catch (err) { /* already finished */ } }); };
    }, [state, width]);

    return (
        <div className="invoice-pdf-viewer" ref={hostRef} data-testid="invoice-pdf-viewer" role="document" aria-label={label}
            style={{ width: '100%', maxWidth: 900, margin: '0 auto', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            {state.status === 'loading' && <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>}
            {Array.from({ length: state.pages }, (_, i) => (
                <canvas key={i} data-page={i + 1} aria-label={`${label} ${i + 1}/${state.pages}`}
                    style={{ display: 'block', background: '#fff', border: '1px solid #dee2e6', boxShadow: '0 1px 4px rgba(0,0,0,.08)', margin: '0 0 12px' }} />
            ))}
            {state.status === 'error' && <div className="text-danger" data-testid="invoice-pdf-error">{label}: PDF could not be shown</div>}
        </div>
    );
}
